import React, { useEffect, useState } from 'react';
import { AppointmentConfirmationCard } from './AppointmentConfirmationCard';
import { Container, ButtonWrapper, Subtitle, CustomCheck, CheckWrapper } from './styles';

import { useContextSelector } from 'use-context-selector';
import { RouterContext } from '../../contexts/RouterContext';
import { PAGES } from '../../Router/pages';
import HeadTitle from '../../components/HeadTitle';
import { CartContext } from '../../contexts/CartContext';
import { PatientContext, PatientProps } from '../../contexts/PatientContext';
import { ScheduleContext } from '../../contexts/ScheduleContext';
import { ScheduleItemProps, typeLocationSchedule } from '../../models/Schedule';
import FooterWrapper from '../../components/FooterWrapper';
import LoadingOverlay from '../../components/LoadingOverlay';
import { PatientScheduleProps } from '../Configurator';
import ServiceItem, { Unit } from '../../components/ServiceItem';
import { ProductItemProps } from '../../models/Product';
import { makeScheduleCard, makeScheduleCardSpecialist } from './AppointmentConfirmationCard/scheduleCard';
import { ItemToScheduleType, useModuleContext } from '../../contexts/ModuleContext';
import ActionButton from './ActionButton';
import ModalTimeNotAvailable from '../../components/ModalTimeNotAvailable';
import { ServiceContext } from '../../contexts/ServiceContext';
import { ScheduleItemPropsBuilder } from '../../models/Schedule';
import ModalScheduleFail from '../../flows/Specialist/components/ModalScheduleFail';
import { CallCenterModal } from '../../components/CallCenterModal';
import { InternalStatusCode } from '../../utils/enums/validate-rules.enum';

export type ScheduleCardProps = {
  unit: Unit;
  schedule: ScheduleItemProps;
  patient: PatientProps;
  product: ProductItemProps;
};

const AppointmentConfirmationList = () => {
  const { goTo, goBack } = useContextSelector(RouterContext, state => state);
  const { scheduleItems, units, setSelectedItem, scheduleSpecialistSelected, selectedItem } = useContextSelector(
    ScheduleContext,
    state => state
  );

  const { selectedItemToSchedule: itemToSchedule } = useModuleContext();

  const { productList } = useContextSelector(CartContext, state => state);
  const patient = useContextSelector(PatientContext, state => state.patientSelected);
  const { selectedService } = useContextSelector(ServiceContext, state => state);
  const [loading, setLoading] = useState(false);
  const [completedSchedules, setCompletedSchedules] = useState<ScheduleCardProps[]>([]);
  const [incompletedSchedules, setIncompletedSchedules] = useState<PatientScheduleProps[]>([]);
  const [modalTimeUnavailable, setModalTimeUnavailable] = useState(false);
  const [modalScheduleFail, setModalScheduleFail] = useState(false);
  const [particularPaymentTermAccepted, setParticularPaymentTermAccepted] = useState(false);

  const [isCallCenterModalVisible, setIsCallCenterModalVisible] = useState(false);

  const IS_SPECIALIST_SCHEDULE = itemToSchedule === 'SPECIALIST';

  function getScheduledProducts() {
    const schedules = [] as ScheduleCardProps[];

    if (patient) {
      scheduleItems.forEach(schedule => {
        if (schedule.idPatient === patient.idPatient) {
          const complete = !!schedule.time && !!schedule?.payment?.type && !!schedule.careType;

          if (complete) {
            const branch = units.find(unit => unit.id_sgh === schedule.unitId);
            const relatedProduct = productList.find(product => product.id === schedule.productId);
            if (relatedProduct) {
              const scheduleConfirmation = makeScheduleCard({
                patient,
                product: relatedProduct,
                schedule,
                unit: branch
              });
              schedules.push(scheduleConfirmation);
            }
          }
        }
      });
    }

    return schedules;
  }

  function getNotScheduledProducts() {
    const patientSchedules = [] as PatientScheduleProps[];

    if (patient) {
      scheduleItems.forEach(schedule => {
        if (schedule.idPatient === patient.idPatient) {
          const scheduleBuild = ScheduleItemPropsBuilder(schedule);
          scheduleBuild.validateSchedule();
          const complete = !!schedule.time && !!schedule?.payment?.type && !!schedule.careType;

          if (!complete) {
            const patientSchedule = {} as PatientScheduleProps;
            patientSchedule.patient = patient;
            patientSchedule.payment = String(schedule?.payment?.type) || '';
            patientSchedule.schedule = schedule;
            patientSchedule.complete = complete;
            const relatedProduct = productList.find(p => p.id === schedule.productId);
            if (relatedProduct) {
              patientSchedule.product = relatedProduct;
            }
            patientSchedules.push(patientSchedule);
          }
        }
      });
    }
    return patientSchedules;
  }

  useEffect(() => {
    if (IS_SPECIALIST_SCHEDULE) {
      if (patient) {
        const infoScheduleCard = makeScheduleCardSpecialist({
          scheduleSpecialist: scheduleSpecialistSelected,
          patient
        });
        setCompletedSchedules([infoScheduleCard]);
      }
    }
    if (
      itemToSchedule === 'PRODUCT' ||
      itemToSchedule === 'PRODUCT_ENV' ||
      itemToSchedule === 'SERVICE_ENV' ||
      itemToSchedule === 'SERVICE'
    ) {
      setCompletedSchedules(getScheduledProducts());
      setIncompletedSchedules(getNotScheduledProducts());
    }
  }, []);

  const handleSubmitError = (codeError?: InternalStatusCode) => {
    switch (codeError) {
      case InternalStatusCode.MEDICAL_RECORD_EXCEPTION:
        return setIsCallCenterModalVisible(true);
      case InternalStatusCode.GENERIC_EXCEPTION:
        return setModalScheduleFail(true);
      default:
        return setModalTimeUnavailable(true);
    }
  };

  const onCloseModalTimeNotAvailable = () => {
    setModalTimeUnavailable(false);
    goTo(PAGES.SERVICE_UNIT.id, {
      scheduleForRemove: true,
      scheduleDateToRemove: scheduleItems[0].time?.slots[0].date,
      scheduleHourToRemove: scheduleItems[0].time?.slots[0].hour
    });
  };

  return (
    <Container>
      <div className="body-wrapper">
        <HeadTitle title="Confirme o agendamento" goBack={goBack} />

        {!!incompletedSchedules.length && <Subtitle>Itens pendentes</Subtitle>}
        {incompletedSchedules.map((schedule, index) => (
          <ServiceItem
            key={index}
            whitoutRemoveBtn
            product={schedule.product}
            handleClick={() => {
              setSelectedItem(schedule.schedule);
              goTo(PAGES.PAYMENT_VIEW.id);
            }}
          />
        ))}

        {/* // TEMPORARY_VACCINE_SCHEDULE */}
        {/* <Subtitle>Pré-agendados</Subtitle> */}
        {completedSchedules.map((scheduleCard, index) => (
          <AppointmentConfirmationCard
            key={index}
            unit={scheduleCard.unit}
            patient={scheduleCard.patient}
            product={scheduleCard.product}
            schedule={scheduleCard.schedule}
            selectedServiceId={selectedService?.id || ''}
          />
        ))}

        {itemToSchedule === ItemToScheduleType.SPECIALIST && (
          <CheckWrapper>
            <CustomCheck
              label="Estou ciente que ao continuar com o agendamento, o pagamento será feito de forma particular."
              labelDirection="right"
              checked={particularPaymentTermAccepted}
              onChange={() => setParticularPaymentTermAccepted(accepted => !accepted)}
            />
          </CheckWrapper>
        )}
      </div>

      <ButtonWrapper>
        <FooterWrapper>
          <ActionButton
            patient={patient}
            setLoading={setLoading}
            completedSchedules={completedSchedules}
            incompletedSchedules={incompletedSchedules}
            handleSubmitErrorCallBack={handleSubmitError}
            disable={itemToSchedule === ItemToScheduleType.SPECIALIST && !particularPaymentTermAccepted}
          />
        </FooterWrapper>
      </ButtonWrapper>
      <LoadingOverlay loading={loading} />
      <ModalTimeNotAvailable visible={modalTimeUnavailable} onClose={onCloseModalTimeNotAvailable} />
      <ModalScheduleFail
        onPressFirstButton={() => {
          goBack();
          setModalScheduleFail(false);
        }}
        visible={modalScheduleFail}
        onClose={() => setModalScheduleFail(false)}
      />
      <CallCenterModal
        visible={isCallCenterModalVisible}
        onClose={() => setIsCallCenterModalVisible(false)}
        serviceId={selectedService.id || ''}
        serviceName={selectedService.name || ''}
        productId={
          IS_SPECIALIST_SCHEDULE ? scheduleSpecialistSelected.resource.professionalId : selectedItem?.productId || ''
        }
        productName={
          IS_SPECIALIST_SCHEDULE ? scheduleSpecialistSelected.resource.name : selectedItem?.productName || ''
        }
        unitId={IS_SPECIALIST_SCHEDULE ? scheduleSpecialistSelected.unit.catalogId : selectedItem?.unitId || ''}
        unitName={IS_SPECIALIST_SCHEDULE ? scheduleSpecialistSelected.unit.name : selectedItem?.unit?.name || ''}
        textMessage={IS_SPECIALIST_SCHEDULE ? 'app_agendamento_especialistas' : ''}
      />
    </Container>
  );
};

export default AppointmentConfirmationList;
